// ==================== COMPONENTE IMPORTAR/EXPORTAR EXCEL ====================
// Permite descargar plantilla Excel, importar subcontratos y materiales masivamente
// y exportar los datos actuales a Excel

import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import api from '../../servicios/api';

// ==================== CONFIGURACIÓN DE COLUMNAS ====================
// Sección y Especialidad son lo mismo, se unifican en una sola columna.
// La columna de fecha acepta una fecha (DD/MM/YYYY) o el texto "Desde Programa"
// (la fila queda pendiente de elegir la partida en la plataforma). La columna
// de Sección puede dejarse vacía (la fila queda pendiente de asignar sección).
const MARCADOR_DESDE_PROGRAMA = 'Desde Programa';
const SECCION_PENDIENTE = 'Pendiente de asignar';

const COLUMNAS_SUBCONTRATOS = [
  'Sección',
  'Descripción',
  'Inicio de Partida (Fecha DD/MM/YYYY o "Desde Programa")'
];

const COLUMNAS_MATERIALES = [
  'Sección',
  'Nombre Material',
  'Inicio de Partida (Fecha DD/MM/YYYY o "Desde Programa")'
];

// ==================== PALETA DE COLORES GESTRACOO ====================
// Colores alineados con el diseño de la app
const COLORS = {
  // Colores principales de la app
  primary: '1F3A5A',        // Azul oscuro principal (#111827)
  primaryLight: '2D4A6D',   // Azul oscuro claro
  accent: 'F28C28',         // Naranja acento (#F28C28)
  accentLight: 'FFF7ED',    // Naranja claro fondo
  accentMid: 'FFEDD5',      // Naranja medio fondo alterno
  // Materiales (azul)
  blue: '1E40AF',           // Azul para materiales
  blueLight: 'EFF6FF',      // Azul claro fondo
  blueMid: 'DBEAFE',        // Azul medio fondo alterno
  // Utilidades
  white: 'FFFFFF',
  grayLight: 'F5F5F5',      // Gris claro para filas alternas
  grayMid: 'E8E8E8',        // Gris medio
  black: '000000',          // Negro puro para texto
  textDark: '1A1A1A',       // Texto oscuro (negro de la app)
  textMid: '6B7280',        // Texto medio
  textLight: '9CA3AF',      // Texto claro
  borderLight: 'E5E7EB',    // Bordes claros
  success: '059669',        // Verde éxito
  successLight: 'F0FDF4',   // Verde claro
  warning: 'D97706',        // Naranja advertencia
  warningLight: 'FFFBEB',   // Amarillo claro (ejemplos)
  error: 'DC2626',          // Rojo error
};

// ==================== ESTILOS REUTILIZABLES ====================
const STYLES = {
  // Título principal de hoja
  title: (bgColor) => ({
    font: { bold: true, sz: 16, color: { rgb: COLORS.white }, name: 'Calibri' },
    fill: { fgColor: { rgb: bgColor } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: bgColor } },
      bottom: { style: 'thin', color: { rgb: bgColor } },
      left: { style: 'thin', color: { rgb: bgColor } },
      right: { style: 'thin', color: { rgb: bgColor } }
    }
  }),
  // Subtítulo descriptivo
  subtitle: (bgColor) => ({
    font: { sz: 10, color: { rgb: COLORS.white }, name: 'Calibri', italic: true },
    fill: { fgColor: { rgb: bgColor } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: bgColor } },
      bottom: { style: 'medium', color: { rgb: COLORS.white } },
      left: { style: 'thin', color: { rgb: bgColor } },
      right: { style: 'thin', color: { rgb: bgColor } }
    }
  }),
  // Encabezados de columna
  header: (bgColor) => ({
    font: { bold: true, sz: 11, color: { rgb: COLORS.white }, name: 'Calibri' },
    fill: { fgColor: { rgb: bgColor } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: COLORS.white } },
      bottom: { style: 'medium', color: { rgb: COLORS.white } },
      left: { style: 'thin', color: { rgb: COLORS.white } },
      right: { style: 'thin', color: { rgb: COLORS.white } }
    }
  }),
  // Celda de datos par (fondo blanco)
  dataEven: () => ({
    font: { sz: 10, color: { rgb: COLORS.black }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.white } },
    alignment: { vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: COLORS.borderLight } },
      bottom: { style: 'thin', color: { rgb: COLORS.borderLight } },
      left: { style: 'thin', color: { rgb: COLORS.borderLight } },
      right: { style: 'thin', color: { rgb: COLORS.borderLight } }
    }
  }),
  // Celda de datos impar (fondo gris claro)
  dataOdd: () => ({
    font: { sz: 10, color: { rgb: COLORS.black }, name: 'Calibri' },
    fill: { fgColor: { rgb: COLORS.grayLight } },
    alignment: { vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: COLORS.borderLight } },
      bottom: { style: 'thin', color: { rgb: COLORS.borderLight } },
      left: { style: 'thin', color: { rgb: COLORS.borderLight } },
      right: { style: 'thin', color: { rgb: COLORS.borderLight } }
    }
  }),
  // Celda de ejemplo (itálica y gris)
  example: {
    font: { sz: 10, color: { rgb: COLORS.textMid }, name: 'Calibri', italic: true },
    fill: { fgColor: { rgb: COLORS.white } },
    alignment: { vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: COLORS.borderLight } },
      bottom: { style: 'thin', color: { rgb: COLORS.borderLight } },
      left: { style: 'thin', color: { rgb: COLORS.borderLight } },
      right: { style: 'thin', color: { rgb: COLORS.borderLight } }
    }
  },
  // Nota de ejemplo
  exampleNote: {
    font: { sz: 9, color: { rgb: COLORS.textMid }, name: 'Calibri', italic: true },
    fill: { fgColor: { rgb: COLORS.grayLight } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: COLORS.borderLight } },
      bottom: { style: 'medium', color: { rgb: COLORS.textLight } },
      left: { style: 'thin', color: { rgb: COLORS.borderLight } },
      right: { style: 'thin', color: { rgb: COLORS.borderLight } }
    }
  }
};

// Función helper para aplicar estilo a un rango
const applyStyleToRange = (ws, startRow, endRow, startCol, endCol, style) => {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
      ws[cellRef].s = style;
    }
  }
};

// ==================== FUNCIONES AUXILIARES ====================

// Convertir fecha Excel (número serial) a Date
const excelDateToJSDate = (serial) => {
  if (serial instanceof Date) return serial;
  if (typeof serial === 'string') {
    const str = serial.trim();
    
    // 1. Intentar parsear DD/MM/YYYY (4 dígitos año)
    const partesFecha4 = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
    if (partesFecha4) {
      const [, p1, p2, año] = partesFecha4;
      // Asumimos DD/MM/YYYY
      return new Date(parseInt(año), parseInt(p2) - 1, parseInt(p1));
    }
    
    // 2. Intentar parsear DD/MM/YY o MM/DD/YY (2 dígitos año)
    const partesFecha2 = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2})$/);
    if (partesFecha2) {
      const [, p1, p2, añoCorto] = partesFecha2;
      const añoNum = parseInt(añoCorto);
      // Año completo: 00-99 -> 2000-2099
      const año = añoNum < 100 ? 2000 + añoNum : añoNum;
      
      // Determinar si es DD/MM o MM/DD analizando los valores
      const num1 = parseInt(p1);
      const num2 = parseInt(p2);
      
      // Si p1 > 12, debe ser día (DD/MM/YY)
      if (num1 > 12) {
        return new Date(año, num2 - 1, num1);
      }
      // Si p2 > 12, debe ser día (MM/DD/YY - formato USA)
      if (num2 > 12) {
        return new Date(año, num1 - 1, num2);
      }
      // Ambos <= 12: asumir DD/MM/YY (formato esperado)
      return new Date(año, num2 - 1, num1);
    }
    
    // 3. Intentar parsear YYYY-MM-DD
    const partesISO = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (partesISO) {
      const [, año, mes, dia] = partesISO;
      return new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
    }
    
    return null;
  }
  if (typeof serial === 'number') {
    // Excel date serial number
    const utc_days = Math.floor(serial - 25569);
    const date = new Date(utc_days * 86400 * 1000);
    return date;
  }
  return null;
};

// Formatear fecha a DD/MM/YYYY para mostrar
const formatDateDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const año = d.getFullYear();
  return `${dia}/${mes}/${año}`;
};

// Formatear fecha a YYYY-MM-DD para enviar al backend
const formatDateBackend = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const año = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${año}-${mes}-${dia}`;
};

// ==================== COMPONENTE PRINCIPAL ====================
const ExcelImportExport = ({ 
  tasks, 
  materiales, 
  sections, 
  materialSections, 
  teamMembers,
  onImportComplete 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('importar'); // 'importar' | 'exportar'
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // ==================== DESCARGAR PLANTILLA EXCEL ====================
  const descargarPlantilla = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // ============================================================
    // HOJA 1: SUBCONTRATOS (Color naranja #F28C28)
    // ============================================================
    const datosSubcontratos = [
      ['GESTRACOO - PLANTILLA DE SUBCONTRATOS', '', ''],
      ['Complete cada fila con los datos de sus subcontratos. Las filas grises son ejemplos que debe reemplazar.', '', ''],
      COLUMNAS_SUBCONTRATOS,
      ['Obra Gruesa', 'Instalación de moldajes sector A', '15/06/2026'],
      ['Electricidad', 'Tendido eléctrico piso 3', 'Desde Programa'],
    ];

    // Agregar 350 filas vacías para pre-formateo
    for (let i = 0; i < 350; i++) {
      datosSubcontratos.push(['', '', '']);
    }

    const wsSubcontratos = XLSX.utils.aoa_to_sheet(datosSubcontratos);

    // Merge título (fila 0)
    wsSubcontratos['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    ];

    // Estilo título
    applyStyleToRange(wsSubcontratos, 0, 0, 0, 2, STYLES.title(COLORS.accent));
    // Estilo subtítulo
    applyStyleToRange(wsSubcontratos, 1, 1, 0, 2, STYLES.subtitle(COLORS.accent));
    // Estilo encabezados (fila 2)
    applyStyleToRange(wsSubcontratos, 2, 2, 0, 2, STYLES.header(COLORS.primary));
    // Estilo filas de ejemplo (filas 3-4) - gris para indicar que son ejemplos
    applyStyleToRange(wsSubcontratos, 3, 3, 0, 2, STYLES.example);
    applyStyleToRange(wsSubcontratos, 4, 4, 0, 2, STYLES.example);

    // Aplicar patrón alterno blanco/gris a las 350 filas (desde fila 5)
    for (let i = 0; i < 350; i++) {
      const row = 5 + i; // Empezar desde fila 5 (índice 0-based)
      const style = i % 2 === 0 ? STYLES.dataEven() : STYLES.dataOdd();
      applyStyleToRange(wsSubcontratos, row, row, 0, 2, style);
    }

    // Ancho de columnas
    wsSubcontratos['!cols'] = [
      { wch: 24 }, { wch: 50 }, { wch: 34 }
    ];
    // Alto de filas
    wsSubcontratos['!rows'] = [
      { hpt: 36 }, { hpt: 22 }, { hpt: 28 }, { hpt: 22 }, { hpt: 22 }
    ];

    XLSX.utils.book_append_sheet(wb, wsSubcontratos, 'Subcontratos');

    // ============================================================
    // HOJA 2: MATERIALES (Color azul #1E40AF)
    // ============================================================
    const datosMateriales = [
      ['GESTRACOO - PLANTILLA DE MATERIALES', '', ''],
      ['Complete cada fila con los datos de sus materiales. Las filas grises son ejemplos que debe reemplazar.', '', ''],
      COLUMNAS_MATERIALES,
      ['Fierro', 'Fierro estriado 12mm', '01/07/2026'],
      ['Hormigón', 'Hormigón H30', 'Desde Programa'],
    ];

    // Agregar 350 filas vacías para pre-formateo
    for (let i = 0; i < 350; i++) {
      datosMateriales.push(['', '', '']);
    }

    const wsMateriales = XLSX.utils.aoa_to_sheet(datosMateriales);

    wsMateriales['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    ];

    applyStyleToRange(wsMateriales, 0, 0, 0, 2, STYLES.title(COLORS.blue));
    applyStyleToRange(wsMateriales, 1, 1, 0, 2, STYLES.subtitle(COLORS.blue));
    applyStyleToRange(wsMateriales, 2, 2, 0, 2, STYLES.header(COLORS.primary));
    applyStyleToRange(wsMateriales, 3, 3, 0, 2, STYLES.example);
    applyStyleToRange(wsMateriales, 4, 4, 0, 2, STYLES.example);

    // Aplicar patrón alterno blanco/gris a las 350 filas (desde fila 5)
    for (let i = 0; i < 350; i++) {
      const row = 5 + i;
      const style = i % 2 === 0 ? STYLES.dataEven() : STYLES.dataOdd();
      applyStyleToRange(wsMateriales, row, row, 0, 2, style);
    }

    wsMateriales['!cols'] = [
      { wch: 24 }, { wch: 50 }, { wch: 34 }
    ];
    wsMateriales['!rows'] = [
      { hpt: 36 }, { hpt: 22 }, { hpt: 28 }, { hpt: 22 }, { hpt: 22 }
    ];

    XLSX.utils.book_append_sheet(wb, wsMateriales, 'Materiales');

    // ============================================================
    // HOJA 3: INSTRUCCIONES (Color azul principal)
    // ============================================================
    const instrucciones = [
      ['INSTRUCCIONES PARA LLENAR LA PLANTILLA GESTRACOO'],
      [''],
      ['SUBCONTRATOS (Hoja 1)'],
      ['Sección: Grupo del subcontrato (ej: Obra Gruesa, Electricidad, OOCC). Puede dejarse vacía: la fila quedará como "Pendiente de asignar" hasta que se le asigne una sección desde la plataforma'],
      ['Descripción: Detalle del trabajo a realizar (máximo 500 caracteres)'],
      ['Inicio de Partida: Fecha en formato DD/MM/YYYY (ej: 15/06/2026), o escribir "Desde Programa" si aún no se sabe la fecha exacta y se va a elegir la partida del Programa después desde la plataforma (la fila queda como "Pendiente")'],
      [''],
      ['MATERIALES (Hoja 2)'],
      ['Sección: Grupo del material (ej: Fierro, Hormigón, Revestimientos). Puede dejarse vacía: la fila quedará como "Pendiente de asignar" hasta que se le asigne una sección desde la plataforma'],
      ['Nombre Material: Descripción del material (máximo 200 caracteres)'],
      ['Inicio de Partida: Fecha de necesidad en formato DD/MM/YYYY (ej: 01/07/2026), o escribir "Desde Programa" igual que en Subcontratos'],
      [''],
      ['NOTAS IMPORTANTES'],
      ['Las filas de ejemplo (en gris cursiva) deben ser eliminadas o reemplazadas con sus datos'],
      ['No modifique los encabezados de las columnas (fila azul)'],
      ['Las fechas de los procesos intermedios se calcularán automáticamente según su configuración'],
      ['Responsable se asigna después desde la plataforma; no forma parte de esta plantilla'],
      ['Puede importar hasta 500 subcontratos y 500 materiales por archivo'],
      ['Las secciones nuevas se crearán automáticamente al importar'],
      ['Las filas marcadas "Desde Programa" y/o sin Sección quedan con un botón "Pendiente" en la matriz hasta que se resuelvan desde la plataforma'],
    ];

    const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
    wsInstrucciones['!cols'] = [{ wch: 90 }];

    // Estilo título instrucciones
    wsInstrucciones['A1'].s = {
      font: { bold: true, sz: 14, color: { rgb: COLORS.white }, name: 'Calibri' },
      fill: { fgColor: { rgb: COLORS.primary } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: { bottom: { style: 'medium', color: { rgb: COLORS.primary } } }
    };
    wsInstrucciones['!rows'] = [{ hpt: 32 }];

    // Subtítulos de secciones
    const seccionRows = [2, 7, 12]; // "SUBCONTRATOS", "MATERIALES", "NOTAS IMPORTANTES"
    seccionRows.forEach(r => {
      const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
      if (wsInstrucciones[cellRef]) {
        wsInstrucciones[cellRef].s = {
          font: { bold: true, sz: 12, color: { rgb: COLORS.white }, name: 'Calibri' },
          fill: { fgColor: { rgb: COLORS.blue } },
          alignment: { vertical: 'center' },
          border: { bottom: { style: 'thin', color: { rgb: COLORS.blue } } }
        };
      }
    });

    // Filas de contenido con fondo suave
    const contentBg = [3,4,5, 8,9,10, 13,14,15,16,17,18,19];
    contentBg.forEach((r, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
      if (wsInstrucciones[cellRef]) {
        wsInstrucciones[cellRef].s = {
          font: { sz: 10, color: { rgb: COLORS.textDark }, name: 'Calibri' },
          fill: { fgColor: { rgb: idx % 2 === 0 ? COLORS.blueLight : COLORS.white } },
          alignment: { vertical: 'center', wrapText: true },
          border: {
            bottom: { style: 'thin', color: { rgb: COLORS.borderLight } }
          }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');

    // Descargar
    XLSX.writeFile(wb, 'Plantilla_GestraCOO.xlsx');
  }, []);

  // ==================== EXPORTAR DATOS ACTUALES A EXCEL ====================
  const exportarDatos = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // ============================================================
    // EXPORTAR SUBCONTRATOS
    // ============================================================
    const headerSub = ['Sección', 'Descripción', 'Inicio de Partida', 'Estado'];
    const filasSub = tasks.map(t => {
      const seccion = t.specialty || t.especialidad || t.section || t.seccion || '';
      return [
        seccion === SECCION_PENDIENTE ? '' : seccion,
        t.text || t.texto || '',
        t.pendiente ? MARCADOR_DESDE_PROGRAMA : formatDateDisplay(t.dueDate || t.fechaEntrega),
        t.completed || t.completada ? 'Completado' : 'Activo'
      ];
    });

    const datosSub = [
      ['GESTRACOO - SUBCONTRATOS DEL PROYECTO', '', '', ''],
      [`Exportado el ${new Date().toLocaleDateString('es-CL')} — ${tasks.length} subcontratos`, '', '', ''],
      headerSub,
      ...filasSub
    ];
    const wsSub = XLSX.utils.aoa_to_sheet(datosSub);

    // Merges
    wsSub['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
    ];

    // Estilos
    applyStyleToRange(wsSub, 0, 0, 0, 3, STYLES.title(COLORS.accent));
    applyStyleToRange(wsSub, 1, 1, 0, 3, STYLES.subtitle(COLORS.accent));
    applyStyleToRange(wsSub, 2, 2, 0, 3, STYLES.header(COLORS.primary));

    // Filas de datos con colores alternos
    for (let i = 0; i < filasSub.length; i++) {
      const row = i + 3;
      const style = i % 2 === 0 ? STYLES.dataEven(COLORS.accentLight) : STYLES.dataOdd(COLORS.accentMid);
      applyStyleToRange(wsSub, row, row, 0, 3, style);

      // Color especial para columna Estado (col 3)
      const estadoRef = XLSX.utils.encode_cell({ r: row, c: 3 });
      if (wsSub[estadoRef]) {
        const isCompleted = wsSub[estadoRef].v === 'Completado';
        wsSub[estadoRef].s = {
          ...style,
          font: { ...style.font, bold: true, color: { rgb: isCompleted ? COLORS.success : COLORS.blue } },
          alignment: { horizontal: 'center', vertical: 'center' }
        };
      }
    }

    wsSub['!cols'] = [
      { wch: 24 }, { wch: 50 }, { wch: 22 }, { wch: 14 }
    ];
    wsSub['!rows'] = [{ hpt: 36 }, { hpt: 22 }, { hpt: 28 }];

    XLSX.utils.book_append_sheet(wb, wsSub, 'Subcontratos');

    // ============================================================
    // EXPORTAR MATERIALES
    // ============================================================
    const headerMat = ['Sección', 'Nombre Material', 'Inicio de Partida', 'Estado'];
    const filasMat = materiales.map(m => {
      return [
        m.specialty === SECCION_PENDIENTE ? '' : (m.specialty || ''),
        m.text || '',
        m.pendiente ? MARCADOR_DESDE_PROGRAMA : formatDateDisplay(m.dueDate),
        m.completed ? 'Completado' : 'Activo'
      ];
    });

    const datosMat = [
      ['GESTRACOO - MATERIALES DEL PROYECTO', '', '', ''],
      [`Exportado el ${new Date().toLocaleDateString('es-CL')} — ${materiales.length} materiales`, '', '', ''],
      headerMat,
      ...filasMat
    ];
    const wsMat = XLSX.utils.aoa_to_sheet(datosMat);

    wsMat['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
    ];

    applyStyleToRange(wsMat, 0, 0, 0, 3, STYLES.title(COLORS.blue));
    applyStyleToRange(wsMat, 1, 1, 0, 3, STYLES.subtitle(COLORS.blue));
    applyStyleToRange(wsMat, 2, 2, 0, 3, STYLES.header(COLORS.primary));

    for (let i = 0; i < filasMat.length; i++) {
      const row = i + 3;
      const style = i % 2 === 0 ? STYLES.dataEven(COLORS.blueLight) : STYLES.dataOdd(COLORS.blueMid);
      applyStyleToRange(wsMat, row, row, 0, 3, style);

      // Color especial para columna Estado (col 3)
      const estadoRef = XLSX.utils.encode_cell({ r: row, c: 3 });
      if (wsMat[estadoRef]) {
        const isCompleted = wsMat[estadoRef].v === 'Completado';
        wsMat[estadoRef].s = {
          ...style,
          font: { ...style.font, bold: true, color: { rgb: isCompleted ? COLORS.success : COLORS.blue } },
          alignment: { horizontal: 'center', vertical: 'center' }
        };
      }
    }

    wsMat['!cols'] = [
      { wch: 24 }, { wch: 50 }, { wch: 22 }, { wch: 14 }
    ];
    wsMat['!rows'] = [{ hpt: 36 }, { hpt: 22 }, { hpt: 28 }];

    XLSX.utils.book_append_sheet(wb, wsMat, 'Materiales');

    // Descargar
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `GestraCOO_Datos_${fecha}.xlsx`);
  }, [tasks, materiales]);

  // ==================== PARSEAR ARCHIVO EXCEL ====================
  const parsearExcel = useCallback((file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        
        const resultado = { subcontratos: [], materiales: [] };
        const errores = [];

        // ---------- Parsear Subcontratos ----------
        const hojaSubcontratos = workbook.Sheets['Subcontratos'] || workbook.Sheets[workbook.SheetNames[0]];
        if (hojaSubcontratos) {
          const rawData = XLSX.utils.sheet_to_json(hojaSubcontratos, { header: 1, raw: false, dateNF: 'dd/mm/yyyy' });
          
          // Detectar fila de encabezados (buscar fila con "Sección" Y "Descripción" juntas)
          let headerRow = 0;
          for (let i = 0; i < Math.min(rawData.length, 10); i++) {
            const row = rawData[i];
            if (!row) continue;
            const rowText = row.map(c => String(c || '').toLowerCase()).join(' ');
            // Buscar fila que tenga tanto "sección" como "descripción" (es el header real)
            if (rowText.includes('sección') && rowText.includes('descripción')) {
              headerRow = i;
              break;
            }
          }
          
          // Iterar desde la fila después de los headers
          for (let i = headerRow + 1; i < rawData.length; i++) {
            const row = rawData[i];
            if (!row || row.length === 0 || !row.some(cell => cell && String(cell).trim())) continue;
            
            // Saltar filas indicadoras o de encabezado
            const primeraCelda = String(row[0] || '').trim().toLowerCase();
            const segundaCelda = String(row[1] || '').trim().toLowerCase();
            if (primeraCelda.includes('ingrese') || primeraCelda.includes('gestracoo') || 
                primeraCelda.includes('complete') || primeraCelda.includes('sección') ||
                segundaCelda.includes('descripción')) continue;

            // Columnas: Sección[0], Descripción[1], Inicio de Partida[2]
            let seccionEspecialidad = String(row[0] || '').trim();
            const descripcion = String(row[1] || '').trim();
            const fechaRaw = row[2];

            // Validar campos requeridos
            if (!descripcion) {
              errores.push({ tipo: 'subcontrato', fila: i + 1, mensaje: 'Falta la descripción' });
              continue;
            }

            let pendiente = false;
            if (!seccionEspecialidad) {
              seccionEspecialidad = SECCION_PENDIENTE;
              pendiente = true;
            }

            // Parsear fecha: acepta una fecha real o el texto "Desde Programa"
            const esDesdePrograma = String(fechaRaw || '').trim().toLowerCase() === MARCADOR_DESDE_PROGRAMA.toLowerCase();
            let fecha;
            if (esDesdePrograma) {
              fecha = new Date();
              pendiente = true;
            } else {
              fecha = excelDateToJSDate(fechaRaw);
              if (!fecha || isNaN(fecha.getTime())) {
                errores.push({ tipo: 'subcontrato', fila: i + 1, mensaje: `Fecha inválida: "${fechaRaw}". Use formato DD/MM/YYYY o "Desde Programa"` });
                continue;
              }
            }

            resultado.subcontratos.push({
              seccion: seccionEspecialidad,
              texto: descripcion,
              especialidad: seccionEspecialidad,
              hechoPor: 'Casa',
              fechaEntrega: formatDateBackend(fecha),
              fechaDisplay: esDesdePrograma ? MARCADOR_DESDE_PROGRAMA : formatDateDisplay(fecha),
              responsable: '',
              pendiente,
              filaOriginal: i + 1
            });
          }
        }

        // ---------- Parsear Materiales ----------
        const hojaMateriales = workbook.Sheets['Materiales'] || workbook.Sheets[workbook.SheetNames[1]];
        if (hojaMateriales) {
          const rawData = XLSX.utils.sheet_to_json(hojaMateriales, { header: 1, raw: false, dateNF: 'dd/mm/yyyy' });
          
          // Detectar fila de encabezados (buscar fila con "Sección" Y "Material" juntas)
          let headerRow = 0;
          for (let i = 0; i < Math.min(rawData.length, 10); i++) {
            const row = rawData[i];
            if (!row) continue;
            const rowText = row.map(c => String(c || '').toLowerCase()).join(' ');
            // Buscar fila que tenga "sección" y "material" como columnas (no en título)
            if (rowText.includes('sección') && rowText.includes('material') && !rowText.includes('gestracoo')) {
              headerRow = i;
              break;
            }
          }
          
          for (let i = headerRow + 1; i < rawData.length; i++) {
            const row = rawData[i];
            if (!row || row.length === 0 || !row.some(cell => cell && String(cell).trim())) continue;
            
            // Saltar filas indicadoras o de encabezado
            const primeraCelda = String(row[0] || '').trim().toLowerCase();
            const segundaCelda = String(row[1] || '').trim().toLowerCase();
            if (primeraCelda.includes('ingrese') || primeraCelda.includes('gestracoo') || 
                primeraCelda.includes('complete') || primeraCelda.includes('sección') ||
                (segundaCelda.includes('material') && segundaCelda.includes('nombre'))) continue;

            // Columnas: Sección[0], Nombre[1], Inicio de Partida[2]
            let seccionEspecialidad = String(row[0] || '').trim();
            const nombre = String(row[1] || '').trim();
            const fechaRaw = row[2];

            if (!nombre) {
              errores.push({ tipo: 'material', fila: i + 1, mensaje: 'Falta el nombre del material' });
              continue;
            }

            let pendiente = false;
            if (!seccionEspecialidad) {
              seccionEspecialidad = SECCION_PENDIENTE;
              pendiente = true;
            }

            const esDesdePrograma = String(fechaRaw || '').trim().toLowerCase() === MARCADOR_DESDE_PROGRAMA.toLowerCase();
            let fecha;
            if (esDesdePrograma) {
              fecha = new Date();
              pendiente = true;
            } else {
              fecha = excelDateToJSDate(fechaRaw);
              if (!fecha || isNaN(fecha.getTime())) {
                errores.push({ tipo: 'material', fila: i + 1, mensaje: `Fecha inválida: "${fechaRaw}". Use formato DD/MM/YYYY o "Desde Programa"` });
                continue;
              }
            }

            resultado.materiales.push({
              seccion: seccionEspecialidad,
              text: nombre,
              specialty: seccionEspecialidad,
              dueDate: formatDateBackend(fecha),
              fechaDisplay: esDesdePrograma ? MARCADOR_DESDE_PROGRAMA : formatDateDisplay(fecha),
              responsable: '',
              pendiente,
              filaOriginal: i + 1
            });
          }
        }

        setPreviewData(resultado);
        setValidationErrors(errores);
        setImportResult(null);
      } catch (err) {
        console.error('Error al parsear Excel:', err);
        setValidationErrors([{ tipo: 'general', fila: 0, mensaje: `Error al leer el archivo: ${err.message}` }]);
      }
    };

    reader.readAsArrayBuffer(file);
  }, []);

  // ==================== MANEJAR ARCHIVO ====================
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar extensión
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(ext)) {
        setValidationErrors([{ tipo: 'general', fila: 0, mensaje: 'Solo se aceptan archivos .xlsx o .xls' }]);
        return;
      }
      parsearExcel(file);
    }
  };

  // Drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parsearExcel(file);
  };

  // ==================== EJECUTAR IMPORTACIÓN ====================
  const ejecutarImportacion = async () => {
    if (!previewData) return;
    
    const erroresCriticos = validationErrors.filter(e => !e.esAdvertencia);
    if (erroresCriticos.length > 0) {
      alert('Hay errores críticos que deben corregirse antes de importar');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const resultado = { subcontratosCreados: 0, materialesCreados: 0, errores: [], seccionesCreadas: [] };

      // Importar subcontratos
      if (previewData.subcontratos.length > 0) {
        const response = await api.post('/subcontratos/importar-masivo', {
          subcontratos: previewData.subcontratos
        });
        resultado.subcontratosCreados = response.data.subcontratosCreados || 0;
        resultado.seccionesCreadas.push(...(response.data.seccionesCreadas || []));
        if (response.data.errores?.length) {
          resultado.errores.push(...response.data.errores.map(e => ({ ...e, tipo: 'subcontrato' })));
        }
      }

      // Importar materiales
      if (previewData.materiales.length > 0) {
        const response = await api.post('/materiales/importar-masivo', {
          materiales: previewData.materiales
        });
        resultado.materialesCreados = response.data.materialesCreados || 0;
        resultado.seccionesCreadas.push(...(response.data.seccionesCreadas || []));
        if (response.data.errores?.length) {
          resultado.errores.push(...response.data.errores.map(e => ({ ...e, tipo: 'material' })));
        }
      }

      setImportResult(resultado);
      
      // Notificar al componente padre para recargar datos
      if (onImportComplete) {
        onImportComplete(resultado);
      }
    } catch (error) {
      console.error('Error en importación:', error);
      setImportResult({
        subcontratosCreados: 0,
        materialesCreados: 0,
        errores: [{ fila: 0, mensaje: error.response?.data?.mensaje || error.message || 'Error de conexión' }]
      });
    } finally {
      setIsImporting(false);
    }
  };

  // ==================== RESETEAR ====================
  const resetear = () => {
    setPreviewData(null);
    setValidationErrors([]);
    setImportResult(null);
    setDragOver(false);
  };

  // ==================== RENDER ====================
  if (!showModal) {
    return (
      <button
        className="excel-import-btn"
        onClick={() => setShowModal(true)}
        title="Importar o exportar datos desde/hacia Excel"
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #2d4a6d 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          padding: '8px 16px',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.2s ease',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
        Excel Import/Export
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    }}
    onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetear(); } }}
    >
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          background: 'linear-gradient(135deg, #111827 0%, #2d4a6d 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Excel Import / Export
          </h2>
          <button
            onClick={() => { setShowModal(false); resetear(); }}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: '#fff',
              fontSize: '1.2rem',
              cursor: 'pointer',
              borderRadius: '4px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >×</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid #e5e7eb',
          background: '#fafafa'
        }}>
          <button
            onClick={() => { setActiveTab('importar'); resetear(); }}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'importar' ? '#ffffff' : 'transparent',
              color: activeTab === 'importar' ? '#111827' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'importar' ? '3px solid #111827' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease'
            }}
          >
            Importar desde Excel
          </button>
          <button
            onClick={() => { setActiveTab('exportar'); resetear(); }}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'exportar' ? '#ffffff' : 'transparent',
              color: activeTab === 'exportar' ? '#111827' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'exportar' ? '3px solid #111827' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease'
            }}
          >
            Exportar a Excel
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, background: '#ffffff' }}>
          {activeTab === 'importar' && (
            <div>
              {/* Paso 1: Descargar plantilla */}
              <div style={{
                background: '#fafafa',
                borderRadius: '6px',
                padding: '16px 20px',
                marginBottom: '16px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#111827', margin: '0 0 8px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                  Paso 1: Descargar plantilla
                </h3>
                <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: '0.85rem' }}>
                  Descarga la plantilla Excel, llénala con los datos de subcontratos y materiales, y luego súbela aquí.
                </p>
                <button
                  onClick={descargarPlantilla}
                  style={{
                    background: 'linear-gradient(135deg, #111827 0%, #2d4a6d 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  Descargar Plantilla Excel
                </button>
              </div>

              {/* Paso 2: Subir archivo */}
              {!importResult && (
                <div style={{
                  background: '#fafafa',
                  borderRadius: '6px',
                  padding: '16px 20px',
                  marginBottom: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h3 style={{ color: '#111827', margin: '0 0 8px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                    Paso 2: Subir archivo Excel
                  </h3>
                  
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: `2px dashed ${dragOver ? '#111827' : '#d1d5db'}`,
                      borderRadius: '6px',
                      padding: '32px',
                      textAlign: 'center',
                      background: dragOver ? '#f3f4f6' : '#ffffff',
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                    onClick={() => document.getElementById('excel-file-input').click()}
                  >
                    <input
                      id="excel-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ marginBottom: '8px' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="12" y1="18" x2="12" y2="12"/>
                      <polyline points="9,15 12,12 15,15"/>
                    </svg>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '0.85rem' }}>
                      Arrastra tu archivo Excel aquí o <span style={{ color: '#111827', textDecoration: 'underline' }}>haz clic para seleccionar</span>
                    </p>
                    <p style={{ color: '#9ca3af', margin: '4px 0 0', fontSize: '0.75rem' }}>
                      Formatos aceptados: .xlsx, .xls
                    </p>
                  </div>
                </div>
              )}

              {/* Preview de datos */}
              {previewData && !importResult && (
                <div style={{
                  background: '#fafafa',
                  borderRadius: '6px',
                  padding: '16px 20px',
                  marginBottom: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h3 style={{ color: '#111827', margin: '0 0 12px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                    Paso 3: Revisar y confirmar
                  </h3>

                  {/* Resumen */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '16px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      flex: 1,
                      minWidth: '120px'
                    }}>
                      <div style={{ color: '#111827', fontWeight: 700, fontSize: '1.4rem' }}>
                        {previewData.subcontratos.length}
                      </div>
                      <div style={{ color: '#374151', fontSize: '0.75rem' }}>Subcontratos</div>
                    </div>
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      flex: 1,
                      minWidth: '120px'
                    }}>
                      <div style={{ color: '#374151', fontWeight: 700, fontSize: '1.4rem' }}>
                        {previewData.materiales.length}
                      </div>
                      <div style={{ color: '#374151', fontSize: '0.75rem' }}>Materiales</div>
                    </div>
                    {validationErrors.filter(e => !e.esAdvertencia).length > 0 && (
                      <div style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        flex: 1,
                        minWidth: '120px'
                      }}>
                        <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '1.4rem' }}>
                          {validationErrors.filter(e => !e.esAdvertencia).length}
                        </div>
                        <div style={{ color: '#dc2626', fontSize: '0.75rem' }}>Errores</div>
                      </div>
                    )}
                    {validationErrors.filter(e => e.esAdvertencia).length > 0 && (
                      <div style={{
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        flex: 1,
                        minWidth: '120px'
                      }}>
                        <div style={{ color: '#d97706', fontWeight: 700, fontSize: '1.4rem' }}>
                          {validationErrors.filter(e => e.esAdvertencia).length}
                        </div>
                        <div style={{ color: '#d97706', fontSize: '0.75rem' }}>Advertencias</div>
                      </div>
                    )}
                  </div>

                  {/* Errores/Advertencias */}
                  {validationErrors.length > 0 && (
                    <div style={{ marginBottom: '16px', maxHeight: '150px', overflowY: 'auto' }}>
                      {validationErrors.map((err, idx) => (
                        <div key={idx} style={{
                          padding: '6px 12px',
                          marginBottom: '4px',
                          borderRadius: '4px',
                          fontSize: '0.78rem',
                          background: err.esAdvertencia ? '#fffbeb' : '#fef2f2',
                          color: err.esAdvertencia ? '#b45309' : '#dc2626',
                          border: `1px solid ${err.esAdvertencia ? '#fde68a' : '#fecaca'}`
                        }}>
                          [{err.tipo}] Fila {err.fila}: {err.mensaje}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tabla preview subcontratos */}
                  {previewData.subcontratos.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ color: '#111827', margin: '0 0 8px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                        Subcontratos a importar:
                      </h4>
                      <div style={{ overflowX: 'auto', maxHeight: '200px', overflowY: 'auto', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '0.75rem'
                        }}>
                          <thead>
                            <tr>
                              {['#', 'Sección', 'Descripción', 'Especialidad', 'Fecha'].map(h => (
                                <th key={h} style={{
                                  padding: '6px 8px',
                                  background: '#111827',
                                  color: '#ffffff',
                                  textAlign: 'left',
                                  borderBottom: '1px solid #e5e7eb',
                                  position: 'sticky',
                                  top: 0,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.subcontratos.slice(0, 50).map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                <td style={{ padding: '5px 8px', color: '#6b7280' }}>{idx + 1}</td>
                                <td style={{ padding: '5px 8px', color: '#1a1a1a' }}>{item.seccion}</td>
                                <td style={{ padding: '5px 8px', color: '#1a1a1a', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.texto}</td>
                                <td style={{ padding: '5px 8px', color: '#4b5563' }}>{item.especialidad}</td>
                                <td style={{ padding: '5px 8px', color: '#4b5563', whiteSpace: 'nowrap' }}>{item.fechaDisplay}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {previewData.subcontratos.length > 50 && (
                          <p style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center', margin: '8px 0' }}>
                            ... y {previewData.subcontratos.length - 50} subcontratos más
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tabla preview materiales */}
                  {previewData.materiales.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ color: '#111827', margin: '0 0 8px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                        Materiales a importar:
                      </h4>
                      <div style={{ overflowX: 'auto', maxHeight: '200px', overflowY: 'auto', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '0.75rem'
                        }}>
                          <thead>
                            <tr>
                              {['#', 'Sección', 'Material', 'Especialidad', 'Fecha'].map(h => (
                                <th key={h} style={{
                                  padding: '6px 8px',
                                  background: '#111827',
                                  color: '#ffffff',
                                  textAlign: 'left',
                                  borderBottom: '1px solid #e5e7eb',
                                  position: 'sticky',
                                  top: 0,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.materiales.slice(0, 50).map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                <td style={{ padding: '5px 8px', color: '#6b7280' }}>{idx + 1}</td>
                                <td style={{ padding: '5px 8px', color: '#1a1a1a' }}>{item.seccion}</td>
                                <td style={{ padding: '5px 8px', color: '#1a1a1a', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</td>
                                <td style={{ padding: '5px 8px', color: '#4b5563' }}>{item.specialty}</td>
                                <td style={{ padding: '5px 8px', color: '#4b5563', whiteSpace: 'nowrap' }}>{item.fechaDisplay}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {previewData.materiales.length > 50 && (
                          <p style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center', margin: '8px 0' }}>
                            ... y {previewData.materiales.length - 50} materiales más
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Botones de acción */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={resetear}
                      style={{
                        background: '#ffffff',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={ejecutarImportacion}
                      disabled={isImporting || validationErrors.filter(e => !e.esAdvertencia).length > 0 || (previewData.subcontratos.length === 0 && previewData.materiales.length === 0)}
                      style={{
                        background: '#ffffff',
                        color: '#111827',
                        border: '1.5px solid #111827',
                        borderRadius: '6px',
                        padding: '10px 24px',
                        cursor: isImporting ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        opacity: (isImporting || validationErrors.filter(e => !e.esAdvertencia).length > 0) ? 0.6 : 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {isImporting ? 'Importando...' : `Importar ${previewData.subcontratos.length + previewData.materiales.length} ítems`}
                    </button>
                  </div>
                </div>
              )}

              {/* Resultado de importación */}
              {importResult && (
                <div style={{
                  background: '#fafafa',
                  borderRadius: '6px',
                  padding: '16px 20px',
                  border: importResult.errores.length > 0 
                    ? '1px solid #fde68a' 
                    : '1px solid #bbf7d0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h3 style={{ 
                    color: importResult.errores.length > 0 ? '#d97706' : '#059669', 
                    margin: '0 0 12px', 
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    fontWeight: 700
                  }}>
                    {importResult.errores.length > 0 ? 'Importación completada con advertencias' : 'Importación exitosa'}
                  </h3>

                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      textAlign: 'center',
                      minWidth: '110px'
                    }}>
                      <div style={{ color: '#111827', fontWeight: 700, fontSize: '1.6rem' }}>
                        {importResult.subcontratosCreados}
                      </div>
                      <div style={{ color: '#374151', fontSize: '0.75rem' }}>Subcontratos</div>
                    </div>
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      textAlign: 'center',
                      minWidth: '110px'
                    }}>
                      <div style={{ color: '#374151', fontWeight: 700, fontSize: '1.6rem' }}>
                        {importResult.materialesCreados}
                      </div>
                      <div style={{ color: '#374151', fontSize: '0.75rem' }}>Materiales</div>
                    </div>
                    {importResult.seccionesCreadas.length > 0 && (
                      <div style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        textAlign: 'center',
                        minWidth: '110px'
                      }}>
                        <div style={{ color: '#111827', fontWeight: 700, fontSize: '1.6rem' }}>
                          {importResult.seccionesCreadas.length}
                        </div>
                        <div style={{ color: '#374151', fontSize: '0.75rem' }}>Secciones nuevas</div>
                      </div>
                    )}
                  </div>

                  {importResult.seccionesCreadas.length > 0 && (
                    <p style={{ color: '#374151', fontSize: '0.8rem', margin: '0 0 8px' }}>
                      Secciones creadas: {importResult.seccionesCreadas.join(', ')}
                    </p>
                  )}

                  {importResult.errores.length > 0 && (
                    <div style={{ maxHeight: '100px', overflowY: 'auto', marginBottom: '12px' }}>
                      {importResult.errores.map((err, idx) => (
                        <div key={idx} style={{
                          padding: '4px 10px',
                          marginBottom: '3px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca'
                        }}>
                          {err.tipo ? `[${err.tipo}]` : ''} Fila {err.fila}: {err.mensaje}
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => { setShowModal(false); resetear(); }}
                    style={{
                      background: 'linear-gradient(135deg, #111827 0%, #2d4a6d 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 24px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      width: '100%',
                      marginTop: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB EXPORTAR */}
          {activeTab === 'exportar' && (
            <div>
              <div style={{
                background: '#fafafa',
                borderRadius: '6px',
                padding: '20px 24px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
              }}>
                <h3 style={{ color: '#111827', margin: '0 0 12px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 700 }}>
                  Exportar datos actuales
                </h3>
                <p style={{ color: '#6b7280', margin: '0 0 16px', fontSize: '0.85rem' }}>
                  Descarga un archivo Excel con todos los subcontratos y materiales actuales del proyecto.
                </p>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#111827', fontWeight: 700, fontSize: '1.4rem' }}>
                      {tasks.length}
                    </div>
                    <div style={{ color: '#374151', fontSize: '0.75rem' }}>Subcontratos</div>
                  </div>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#374151', fontWeight: 700, fontSize: '1.4rem' }}>
                      {materiales.length}
                    </div>
                    <div style={{ color: '#374151', fontSize: '0.75rem' }}>Materiales</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={exportarDatos}
                    disabled={tasks.length === 0 && materiales.length === 0}
                    style={{
                      background: '#ffffff',
                      color: tasks.length === 0 && materiales.length === 0 ? '#9ca3af' : '#111827',
                      border: '1.5px solid #111827',
                      borderRadius: '6px',
                      padding: '12px 24px',
                      cursor: tasks.length === 0 && materiales.length === 0 ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      opacity: tasks.length === 0 && materiales.length === 0 ? 0.5 : 1,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    Exportar Datos del Proyecto
                  </button>
                  <button
                    onClick={descargarPlantilla}
                    style={{
                      background: 'linear-gradient(135deg, #111827 0%, #2d4a6d 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 24px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    Descargar Plantilla Vacía
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportExport;
